import React, { useCallback, useRef, useState } from 'react'

import { Button, Icon, Popover } from '@clientbase/clientbase-library'
import { Box, Collapse, PopoverActions, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import {
  FieldType,
  useCustomFieldPermissions
} from 'hooks/useCustomFieldPermissions'
import { isCNPJ } from 'templates/RichTextEditor/utils'

import { theme } from 'styles/theme'

import {
  AddedTag,
  CategoryButton,
  CategoryDivider,
  CategoryIcon,
  Chevron,
  ItemButton,
  ItemLabel,
  MenuContainer,
  ScrollArea,
  StateMessage
} from './ChargeVariablePicker.styles'
import {
  getChargeVariables,
  RECURRENCE_CATEGORY_KEY,
  VariableItem
} from './chargeVariables'

type CustomFieldFromAPI = {
  uuid: string
  field_type: string
  field_configuration: {
    title: string
  }
}

type ChargeVariablePickerProps = {
  description: string
  setDescription: (value: string) => void
  isRecurrence: boolean
  onAddItemsVariable?: () => void
  hasItemsVariable?: boolean
  /** Insere a variável no cursor do campo (ver `useInsertAtCursor`). */
  insertAtCursor?: (current: string, text: string) => string
}

const ITEMS_TOKEN = '{{itens_da_cobranca}}'

const ChargeVariablePicker = ({
  description,
  setDescription,
  isRecurrence,
  onAddItemsVariable,
  hasItemsVariable = false,
  insertAtCursor = (current, text) => current + text
}: ChargeVariablePickerProps) => {
  const { getFieldTypePermission } = useCustomFieldPermissions()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  // Mantém o popover ancorado ao botão enquanto a altura muda (abrir/fechar
  // seção). Sem isso, o Popover fixa o `top` no open e "pula" ao expandir,
  // porque abre pra cima (transformOrigin bottom → top depende da altura).
  const popoverActionRef = useRef<PopoverActions>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const setMenuRef = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect()

    if (node && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        if (popoverActionRef.current) {
          popoverActionRef.current.updatePosition()
        } else {
          // Fallback: se `action` não for repassado, o Popover ainda
          // reposiciona ao ouvir o resize da janela.
          window.dispatchEvent(new Event('resize'))
        }
      })
      observer.observe(node)
      resizeObserverRef.current = observer
    }
  }, [])
  const [customFields, setCustomFields] = useState<VariableItem[]>([])
  const [loadingCustomFields, setLoadingCustomFields] = useState(false)

  const loadCustomFields = async () => {
    setLoadingCustomFields(true)
    try {
      const response = await DataService({
        type: 'GET',
        url: '/v1/custom_fields/all'
      })

      if (response.status === 200 && response.data?.records) {
        const fields = response.data.records as CustomFieldFromAPI[]
        const mapped = fields
          .filter(
            (field) =>
              getFieldTypePermission(field.field_type as FieldType).hasAccess
          )
          .map((field) => ({
            label: field.field_configuration.title,
            token: `{{campo_extra:"${field.field_configuration.title}"}}`
          }))
        setCustomFields(mapped)
      } else {
        setCustomFields([])
      }
    } catch (error) {
      console.error('Erro ao carregar campos extras:', error)
      setCustomFields([])
    } finally {
      setLoadingCustomFields(false)
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    loadCustomFields()
  }

  const handleClose = () => {
    setAnchorEl(null)
    setExpandedKey(null)
  }

  const handleClickItem = (token: string) => {
    // Não permitir adicionar {{itens_da_cobranca}} se já existe
    if (token === ITEMS_TOKEN && hasItemsVariable) {
      handleClose()
      return
    }

    setDescription(insertAtCursor(description, token))

    // Se a variável adicionada for {{itens_da_cobranca}}, chama o callback
    if (token === ITEMS_TOKEN && onAddItemsVariable) {
      onAddItemsVariable()
    }

    handleClose()
  }

  const categories = getChargeVariables({
    isCNPJ: isCNPJ(),
    customFields
  }).filter(
    (category) => isRecurrence || category.key !== RECURRENCE_CATEGORY_KEY
  )

  const open = Boolean(anchorEl)
  const id = open ? 'charge-variable-picker' : undefined

  return (
    <Box mt={0.5}>
      <Box display="flex" alignItems="center" justifyContent="flex-end">
        <Button startIcon="add" variantButton="ghost" onClick={handleClick}>
          Adicionar variável
        </Button>
      </Box>

      <Popover
        action={popoverActionRef}
        // Não devolve o foco pro botão: o `insertAtCursor` devolve pro campo.
        disableRestoreFocus
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        anchorEl={anchorEl}
        id={id}
        open={open}
        onClose={handleClose}
      >
        <MenuContainer ref={setMenuRef} role="menu">
          <ScrollArea>
            {categories.map((category, index) => {
              const isExpanded = expandedKey === category.key
              const isCustomFields = category.key === 'campos_extras'
              const isEmpty = category.items.length === 0
              const panelId = `charge-variable-panel-${category.key}`

              return (
                <Box key={category.key}>
                  {index > 0 && <CategoryDivider />}

                  <CategoryButton
                    type="button"
                    role="menuitem"
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    $expanded={isExpanded}
                    onClick={() =>
                      setExpandedKey(isExpanded ? null : category.key)
                    }
                  >
                    <CategoryIcon>
                      <Icon
                        icon={category.icon}
                        width="18px"
                        height="18px"
                        color="inherit"
                      />
                    </CategoryIcon>

                    <Typography
                      variant="smBold"
                      color={theme.palette.neutral[100]}
                    >
                      {category.label}
                    </Typography>

                    <Chevron $expanded={isExpanded}>
                      <Icon
                        icon="keyboardArrowRight"
                        width="20px"
                        height="20px"
                        color="inherit"
                      />
                    </Chevron>
                  </CategoryButton>

                  <Collapse in={isExpanded} unmountOnExit>
                    <Box id={panelId}>
                      {isCustomFields && loadingCustomFields && (
                        <StateMessage>Carregando...</StateMessage>
                      )}

                      {isCustomFields && !loadingCustomFields && isEmpty && (
                        <StateMessage>
                          Nenhum campo extra disponível
                        </StateMessage>
                      )}

                      {!isEmpty &&
                        category.items.map((item) => {
                          const isItemsVariable = item.token === ITEMS_TOKEN
                          const isDisabled = isItemsVariable && hasItemsVariable

                          return (
                            <ItemButton
                              key={item.token}
                              type="button"
                              role="menuitem"
                              $disabled={isDisabled}
                              disabled={isDisabled}
                              onClick={() => handleClickItem(item.token)}
                            >
                              <ItemLabel
                                $disabled={isDisabled}
                                title={item.label}
                              >
                                {item.label}
                              </ItemLabel>
                              {isDisabled && <AddedTag>Adicionado</AddedTag>}
                            </ItemButton>
                          )
                        })}
                    </Box>
                  </Collapse>
                </Box>
              )
            })}
          </ScrollArea>
        </MenuContainer>
      </Popover>
    </Box>
  )
}

export default ChargeVariablePicker

export { ALL_CHARGE_TOKENS, RECURRENCE_TOKENS } from './chargeVariables'
